import type { Session, SupabaseClient } from '@supabase/supabase-js';

export type PasswordRecoveryResult =
  | { session: Session; error: null }
  | { session: null; error: Error };

const AUTH_URL_PARAMS = [
  'access_token',
  'auth_code',
  'code',
  'error',
  'error_code',
  'error_description',
  'expires_at',
  'expires_in',
  'provider_refresh_token',
  'provider_token',
  'refresh_token',
  'token_type',
  'type',
];

const delay = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const normalizeError = (error: unknown, fallbackMessage: string) => (
  error instanceof Error ? error : new Error(fallbackMessage)
);

const getBrowserAuthParams = () => {
  const params = new URLSearchParams(window.location.search);
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;

  if (hash) {
    const hashParams = new URLSearchParams(hash);
    hashParams.forEach((value, key) => {
      params.set(key, value);
    });
  }

  return params;
};

const cleanupAuthParamsFromUrl = () => {
  const url = new URL(window.location.href);
  AUTH_URL_PARAMS.forEach((param) => url.searchParams.delete(param));
  url.hash = '';

  const cleanUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', cleanUrl);
};

const getUrlAuthError = (params: URLSearchParams) => {
  const description = params.get('error_description') || params.get('error');
  if (!description) return null;
  const code = params.get('error_code');
  return new Error(code ? `${description} (${code})` : description);
};

const waitForSession = async (
  supabase: SupabaseClient,
  timeoutMs: number,
  pollIntervalMs: number,
): Promise<{ session: Session | null; error: Error | null }> => {
  const startedAt = Date.now();

  do {
    const { data, error } = await supabase.auth.getSession();
    if (error) return { session: null, error: normalizeError(error, 'Unable to verify reset link') };
    if (data.session) return { session: data.session, error: null };
    await delay(pollIntervalMs);
  } while (Date.now() - startedAt < timeoutMs);

  return { session: null, error: null };
};

export const getPasswordResetRedirectUrl = (
  path = '/reset-password',
  options: {
    origin?: string;
  } = {},
) => {
  const origin = options.origin || (typeof window === 'undefined' ? undefined : window.location.origin);
  if (!origin) {
    throw new Error('Password reset redirect URL requires a browser origin or explicit origin');
  }
  return new URL(path, origin).toString();
};

export async function resolvePasswordRecoverySession(
  supabase: SupabaseClient,
  options: {
    cleanupUrl?: boolean;
    pollIntervalMs?: number;
    timeoutMs?: number;
  } = {},
): Promise<PasswordRecoveryResult> {
  const { cleanupUrl = true, pollIntervalMs = 100, timeoutMs = 2500 } = options;

  if (typeof window === 'undefined') {
    return { session: null, error: new Error('Password recovery requires a browser session') };
  }

  const params = getBrowserAuthParams();
  const urlError = getUrlAuthError(params);
  if (urlError) {
    return { session: null, error: urlError };
  }

  const code = params.get('code') || params.get('auth_code');
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');

  try {
    if (code) {
      const { data, error } = await supabase.auth.exchangeCodeForSession(code);
      if (error) return { session: null, error: normalizeError(error, 'Unable to verify reset link') };
      if (cleanupUrl) cleanupAuthParamsFromUrl();
      if (data.session) return { session: data.session, error: null };
    }

    if (accessToken && refreshToken) {
      const { data, error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      if (error) return { session: null, error: normalizeError(error, 'Unable to verify reset link') };
      if (cleanupUrl) cleanupAuthParamsFromUrl();
      if (data.session) return { session: data.session, error: null };
    }

    const { session, error } = await waitForSession(supabase, timeoutMs, pollIntervalMs);
    if (error) return { session: null, error };
    if (session) {
      if (cleanupUrl && (accessToken || refreshToken || code)) cleanupAuthParamsFromUrl();
      return { session, error: null };
    }

    return { session: null, error: new Error('Invalid or expired reset link') };
  } catch (error) {
    return {
      session: null,
      error: error instanceof Error ? error : new Error('Unable to verify reset link'),
    };
  }
}
